import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import NativeCheckoutDialog from "src/components/blocks/dialogs/NativeCheckoutDialog.js";
import { LoadingButton } from "../../blocks/LoadingButton.js";
import { Typography, Box, Icon } from "@mui/material";
import { getPageRoute } from "src/services";
import TransparentUpdatesExplainer from "src/components/explainers/TransparentUpdatesExplainer.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import { useDimensions } from "src/utils/useDimensions.js";
import ActionOption from "src/components/blocks/options/ActionOption.js";
import OpenCalendarDialog from "../../blocks/dialogs/OpenCalendarDialog.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { customEvent } from "src/utils/gtag.js";


const CalendarSteps = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const theme = useTheme();
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const { subscribe } = useSubscribe();
  const { isMediumScreen, isExtraSmallScreen } = useDimensions();
  const [actionSelected, setActionSelected] = useState(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);

  useEffect(() => {
    customEvent({
      name: "landed_on_other_calendar_steps",
      category: "OtherCalendarSteps",
      label: "Landed on Other Calendar Steps",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  const CheckIcon = () => (
    <Icon
      baseClassName="material-symbols-outlined"
      sx={{
        display: "inline",
        color: `${theme.palette.icon.primary}`,
        fontSize: "1rem",
        verticalAlign: "middle",
      }}
    >
      check_small
    </Icon>
  );

  const actionOptions = [
    {
      id: "sync-automatically",
      title: "Connect your Calendar",
      subtitle: (
        <>
          <CheckIcon />
          Updates in near-real time. <CheckIcon />
          Syncs across all your devices. <CheckIcon />
          Always know when your calendar last refreshed.
        </>
      ),
      isPopular: true,
      isDefault: true,
      showAnimation: true,
    },
    {
      id: "status-quo", 
      title: "Wait and Wonder",
      subtitle: `Your calendar may be 1–2 days behind and you won’t know when it’s missing updates.`,
      isPopular: false,
      isDefault: false,
      showAnimation: false,
    },
  ];

  return (
    <InstructionsLayout nav="back">
      <Box sx={{ mb: 2.5 }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            mr: "auto",
            color: `${theme.palette.icon.primary}`,
            fontSize: "1.6rem",
            mb: 1,
            fontWeight: 300,
          }}
        >
          event_upcoming
        </Icon>
        <Typography
          variant="h1"
          sx={{
            mr: "auto",
            textAlign: "left",
            fontSize: "2rem !important",
            lineHeight: "1.4 !important",
            fontWeight: "500 !important",
          }}
        >
          Your calendar updates silently.
        </Typography>
      </Box>

      {user && !user.hasValidSubscription ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {actionOptions.map((option) => (
            <ActionOption
              key={option.id}
              {...option}
              isSelected={option.id === actionSelected}
              onSelect={(id) => {
                setActionSelected(id);
                setTimeout(() => {
                  if (id === "sync-automatically") {
                    setOpenPaymentDialog(true);
                  } else if (id === "status-quo") {
                    subscribe({ calendar });
                    setOpenCalendarDialog(true);
                  }
                }, 500);
              }}
            >
              {/* Show animation only for sync-automatically option */}
              {option.showAnimation && (
                <Box 
                  sx={{ 
                    maxWidth: "380px",
                    mx: "auto",
                    overflow: "hidden"
                  }}
                >
                  <TransparentUpdatesExplainer
                    onUpgradeClick={() => {
                      // Handle upgrade action based on user subscription status
                      if (user && !user.hasValidSubscription) {
                        // Will be handled by UpgradeConsumerButton below
                      } else {
                        navigate(getPageRoute("help", "CALENDAR_CONNECTED"));
                      }
                    }}
                    onSeeAllBenefits={() => {
                      // Handle see all benefits action
                      if (user && !user.hasValidSubscription) {
                        // Could navigate to benefits page
                      }
                    }}
                  />
                </Box>
              )}
            </ActionOption>
          ))}
        </Box>
      ) : (
        <>
          <Typography
            variant={"subtitle2"}
            sx={{ textAlign: "left", mr: "auto", mt: 1 }}
            color="text.secondary"
          >
            Members save time and are the first to know.
          </Typography>
          <LoadingButton
            size="grown"
            variant="contained"
            color="primary"
            sx={{ mt: 2, height: "auto", width: "100%" }}
            onClick={() => navigate(getPageRoute("help", "CALENDAR_CONNECTED"))}
          >
            Get Started
          </LoadingButton>
        </>
      )}
      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={calendar}
      />
      <NativeCheckoutDialog
        open={!!openPaymentDialog}
        successUrl={getPageRoute("help", "CALENDAR_CONNECTED")}
        calendar={calendar}
        onClose={() => {
          setOpenPaymentDialog(false);
          setActionSelected(null);
        }}
      />
    </InstructionsLayout>
  );
};

export default CalendarSteps;
